import type { LogRocketIdentityInput } from './logrocket-identity';
import { buildLogRocketIdentityPayload } from './logrocket-identity';

type LogRocketTrackValue = string | number | boolean | string[] | number[] | boolean[] | null | undefined;
type LogRocketTrackPayload = Record<string, LogRocketTrackValue>;

type LogRocketRequestOrResponse = {
  url?: string;
  method?: string;
  headers?: Record<string, string>;
  body?: unknown;
  status?: number;
};

type LogRocketSdk = {
  init: (appId: string, options?: Record<string, unknown>) => void;
  identify: (userId: string, traits?: Record<string, unknown>) => void;
  track: (name: string, payload?: Record<string, unknown>) => void;
  getSessionURL?: (callback: (sessionURL: string) => void) => void;
  sessionURL?: string | null;
};

type LogRocketActivationSource = 'production-runtime' | 'browser-load-audit' | 'disabled';
type LogRocketInitStatus = 'disabled' | 'loading' | 'ready' | 'error';

type LogRocketInitState = {
  enabled: boolean;
  activationSource: LogRocketActivationSource;
  status: LogRocketInitStatus;
  appId: string | null;
  policyId: string;
  runId: string | null;
  error: string | null;
};

declare global {
  interface Window {
    __TRIEDGE_LOGROCKET_AUDIT__?: {
      status: () => LogRocketInitState;
      track: (eventName: string, payload?: LogRocketTrackPayload) => Record<string, unknown>;
      getSessionURL: () => string | null;
    };
  }
}

const REDACTED = '[redacted]';
const SENSITIVE_HEADERS = new Set([
  'authorization',
  'cookie',
  'set-cookie',
  'x-csrf-token',
  'x-xsrf-token',
  'x-api-key',
  'api-key',
  'apikey',
  'x-amz-security-token',
]);
const SENSITIVE_QUERY_KEYS = new Set([
  'token',
  'id_token',
  'access_token',
  'refresh_token',
  'code',
  'state',
  'session',
  'password',
  'secret',
  'key',
  'api_key',
]);

let sdkPromise: Promise<LogRocketSdk | null> | null = null;
let sdk: LogRocketSdk | null = null;
let cachedSessionURL: string | null = null;
let initAttemptKey: string | null = null;
let lastIdentityDedupeKey: string | null = null;
let initState: LogRocketInitState = {
  enabled: false,
  activationSource: 'disabled',
  status: 'disabled',
  appId: null,
  policyId: 'network-detail',
  runId: null,
  error: null,
};

const TRUE_VALUES = new Set(['1', 'true', 'yes', 'on']);

const normalizeText = (value: unknown): string => String(value ?? '').trim();
const isTruthyEnv = (value: unknown): boolean => TRUE_VALUES.has(normalizeText(value).toLowerCase());

const telemetryMode = normalizeText(import.meta.env.VITE_BROWSER_LOAD_AUDIT_TELEMETRY_MODE);
const appId = normalizeText(import.meta.env.VITE_YSTNJ_LOGROCKET_APP_ID);
const runId = normalizeText(import.meta.env.VITE_BROWSER_LOAD_AUDIT_RUN_ID) || null;
const explicitRuntimeEnabled = isTruthyEnv(import.meta.env.VITE_YSTNJ_LOGROCKET_ENABLED);
const auditSidecarEnabled = telemetryMode === 'logrocket';
const activationSource: LogRocketActivationSource = explicitRuntimeEnabled
  ? 'production-runtime'
  : auditSidecarEnabled
    ? 'browser-load-audit'
    : 'disabled';
const policyId = normalizeText(import.meta.env.VITE_YSTNJ_LOGROCKET_POLICY_ID)
  || (activationSource === 'production-runtime' ? 'strict-sanitize' : 'network-detail');
const shouldUseLogRocket = () => activationSource !== 'disabled' && Boolean(appId);
const missingAppIdError = () => (
  activationSource !== 'disabled' && !appId
    ? 'VITE_YSTNJ_LOGROCKET_APP_ID is required when LogRocket telemetry is enabled.'
    : null
);

const sanitizeUrl = (value: unknown): string => {
  const raw = String(value ?? '');
  if (!raw) return raw;
  try {
    const parsed = new URL(
      raw,
      typeof window === 'undefined' ? 'https://ystnj.local' : window.location.origin,
    );
    for (const key of [...parsed.searchParams.keys()]) {
      if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) {
        parsed.searchParams.set(key, REDACTED);
      }
    }
    return parsed.href;
  } catch {
    return raw;
  }
};

const sanitizeHeaders = (headers: LogRocketRequestOrResponse['headers']) => {
  if (!headers || typeof headers !== 'object') return headers;
  return Object.fromEntries(
    Object.entries(headers).map(([key, value]) => [
      key,
      SENSITIVE_HEADERS.has(key.toLowerCase()) ? REDACTED : value,
    ]),
  );
};

const sanitizeJsonShape = (value: unknown): unknown => {
  if (value == null) return value;
  if (typeof value === 'string') {
    try {
      return JSON.stringify(sanitizeJsonShape(JSON.parse(value))).slice(0, 4000);
    } catch {
      return REDACTED;
    }
  }
  if (Array.isArray(value)) {
    return value.slice(0, 20).map((entry) => sanitizeJsonShape(entry));
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).slice(0, 50);
    return Object.fromEntries(entries.map(([key, entry]) => {
      if (key === 'operationName') return [key, String(entry ?? '')];
      if (entry && typeof entry === 'object') return [key, sanitizeJsonShape(entry)];
      return [key, REDACTED];
    }));
  }
  return REDACTED;
};

const sanitizeNetworkPayload = (
  payload: LogRocketRequestOrResponse,
): LogRocketRequestOrResponse | null => {
  if (!payload) return payload;
  const strict = policyId === 'strict-sanitize';
  return {
    ...payload,
    url: sanitizeUrl(payload.url),
    headers: sanitizeHeaders(payload.headers),
    body: strict ? undefined : sanitizeJsonShape(payload.body),
  };
};

const loadSdk = async (): Promise<LogRocketSdk | null> => {
  if (sdk) return sdk;
  if (sdkPromise) return sdkPromise;
  sdkPromise = import('logrocket')
    .then((module) => {
      const loaded = (module.default ?? module) as LogRocketSdk;
      sdk = loaded;
      return loaded;
    })
    .catch((error: unknown) => {
      initState = {
        ...initState,
        status: 'error',
        error: error instanceof Error ? error.message : String(error),
      };
      return null;
    });
  return sdkPromise;
};

const ensureAuditBridge = () => {
  if (typeof window === 'undefined') return;
  window.__TRIEDGE_LOGROCKET_AUDIT__ = {
    status: () => ({ ...initState }),
    track: (eventName: string, payload?: LogRocketTrackPayload) => {
      trackLogRocketEvent(eventName, payload);
      return { status: initState.status };
    },
    getSessionURL: () => cachedSessionURL ?? sdk?.sessionURL ?? null,
  };
};

export const initLogRocket = (): void => {
  ensureAuditBridge();
  const appIdRequiredError = missingAppIdError();

  if (appIdRequiredError) {
    initState = {
      enabled: false,
      activationSource,
      status: 'error',
      appId: null,
      policyId,
      runId,
      error: appIdRequiredError,
    };
    return;
  }

  if (!shouldUseLogRocket()) {
    initState = {
      enabled: false,
      activationSource: 'disabled',
      status: 'disabled',
      appId: null,
      policyId,
      runId,
      error: null,
    };
    return;
  }

  const nextInitKey = `${activationSource}:${appId}:${policyId}:${runId ?? ''}`;
  if (initAttemptKey === nextInitKey && initState.status !== 'error') {
    return;
  }
  initAttemptKey = nextInitKey;
  initState = {
    enabled: true,
    activationSource,
    status: 'loading',
    appId,
    policyId,
    runId,
    error: null,
  };

  void loadSdk().then((loaded) => {
    if (!loaded) return;
    try {
      loaded.init(appId, {
        release: runId ? `browser-load-audit:${runId}` : undefined,
        dom: {
          inputSanitizer: true,
          textSanitizer: policyId === 'strict-sanitize',
        },
        network: {
          requestSanitizer: sanitizeNetworkPayload,
          responseSanitizer: sanitizeNetworkPayload,
        },
      });
      loaded.getSessionURL?.((sessionURL) => {
        cachedSessionURL = sessionURL || null;
      });
      initState = {
        enabled: true,
        activationSource,
        status: 'ready',
        appId,
        policyId,
        runId,
        error: null,
      };
    } catch (error) {
      initState = {
        enabled: true,
        activationSource,
        status: 'error',
        appId,
        policyId,
        runId,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  });
};

export const clearLogRocketIdentity = () => {
  lastIdentityDedupeKey = null;
  return undefined;
};

export const identifyLogRocketUser = (input: LogRocketIdentityInput) => {
  if (!shouldUseLogRocket()) return undefined;
  const payload = buildLogRocketIdentityPayload(input);
  if (!payload) return undefined;
  if (lastIdentityDedupeKey === payload.dedupeKey) return undefined;
  lastIdentityDedupeKey = payload.dedupeKey;
  void loadSdk().then((loaded) => {
    if (loaded && initState.status === 'ready') {
      loaded.identify(payload.userId, payload.traits);
    }
  });
  return undefined;
};

export const trackLogRocketEvent = (
  name: string,
  payload?: LogRocketTrackPayload,
) => {
  if (!shouldUseLogRocket()) return undefined;
  const eventName = normalizeText(name);
  if (!eventName) return undefined;
  void loadSdk().then((loaded) => {
    if (loaded && initState.status === 'ready') {
      loaded.track(eventName, {
        ...payload,
        app: 'ystnj',
        auditRunId: runId,
        telemetryMode,
      });
    }
  });
  return undefined;
};
